/**
 * Client-side VYSOR scope check — mirrors backend vysorMessageScope for instant UX feedback.
 */

export const VYSOR_OUT_OF_SCOPE_REPLY =
  "I'm VYSOR — I help with identity and access in this workspace (company login, apps, and who can access them).\n\n" +
  "I'm not the right place for weather, sports, or general coding.\n\n" +
  'Try something like “Set up login for Salesforce”, “Add a new app”, or “Who can access Adobe?” — or use the quick actions above.';

const WIZARD_HEADER = /\[VYSOR wizard\s*—/i;
const VYSOR_CONTEXT_HEADER = /\[VYSOR context:/i;

const GREETING_RE =
  /^(hi|hello|hey|good\s+(morning|afternoon|evening)|howdy|greetings|yo)\b[!.?\s]*$/i;

const VYSOR_HELP_RE =
  /^(help|what can you do|what do you do|who are you|how do i use (this|vysor)|what is vysor|how can i get started)\b/i;

const CONVERSATIONAL_GUIDE_RE =
  /^(how (can|do|does|to|should)|what('?s| is) the (best )?way|can you (help|show|tell|walk)|help me|i (want|need) to|i'?m trying to|walk me through|explain( how)?|show me how)\b/i;

const CONFUSION_RE =
  /\b(i\s+)?(do\s+not|don'?t)\s+(understand|know)\b/i;

const UNCERTAIN_RE =
  /\b(i'?m\s+)?(not sure|unsure|lost|confused)\b/i;

const SOCIAL_ACK_RE =
  /^(thanks|thank you|thx|ok|okay|got it|great|perfect|sounds good|cool|yes|yeah|yep|sure)\b[!.?\s]*$/i;

const VYSOR_COMMAND_RE =
  /^(full|all|next|more|continue|previous|prev|back|clear filter|reset filter|show all pending|remove filter|list pending( sso)?|show pending( sso)?|filter( by)?( .+)?|narrow( to)?( .+)?|staging|production|prod|development|dev|test|qa|uat|sandbox|demo|saml|oidc|openid|pkce|not sure|help me choose|open (the )?sso form|browse applications?|change app|pick (a )?different app|submit|review)\b/i;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const OFF_TOPIC_RE: RegExp[] = [
  /\bweather\b/i,
  /\bforecast\b/i,
  /\b(temperature|rain|snow|humidity)\b.*\b(today|tomorrow|outside|weekend)\b/i,
  /\bhow\s+(hot|cold)\s+is\s+it\b/i,
  /\b(news|headlines|current events)\b/i,
  /\b(sports?|football|soccer|cricket|nba|nfl|world cup)\b/i,
  /\b(recipe|cook|bake|pizza|restaurant recommendation)\b/i,
  /\b(movie|netflix|tv show|celebrity gossip)\b/i,
  /\b(bitcoin|crypto|stock price|share price|dow jones)\b/i,
  /\b(write|generate|create)\s+(me\s+)?(a\s+)?(poem|story|joke|song|essay)\b/i,
  /\b(write|generate|create)\s+(me\s+)?(a\s+)?(python|javascript|java|react|node\.?js)\s+(script|program|app|code)\b/i,
  /\bwhat\s+is\s+the\s+capital\s+of\b/i,
  /\btranslate\s+.+\s+to\s+(french|spanish|hindi|german)\b/i,
];

const IAM_KEYWORDS = [
  'sso', 'saml', 'oidc', 'oauth', 'openid', 'identity', 'iam', 'iga', 'pam', 'entitlement',
  'application', 'app', 'metadata', 'okta', 'azure', 'entra', 'idp', 'login', 'sign-on',
  'access', 'role', 'permission', 'risk', 'onboard', 'configure', 'provysn', 'turboiam',
  'vysor', 'entity id', 'acs', 'client_id', 'redirect', 'mfa', 'authentication', 'catalog',
  'instance', 'environment', 'federation', 'privileged', 'vault', 'pending', 'staging',
  'production',
  'protocol',
  'workday',
  'submit',
  'review',
  'fill',
  'details',
  'default',
  'defaults',
  'oracle',
  'SSO',
  'field',
  'fields',
  'mandatory',
  'optional',
  'required',
  'secret',
  'secrets',
  'credential',
  'credentials',
  'password',
  'private key',
  'token',
  'bearer',
  'questionnaire',
  'checklist',
  'vendor',
  'step by step',
  'one question',
  'one step',
  'non-technical',
  'not technical',
  'sensitive',
];

const ONBOARDING_HELP_RE =
  /\b(one\s+(question|step)\s+at\s+a\s+time|step[\s-]by[\s-]step|not\s+technical|non-?technical|all\s+the\s+fields|mandatory|optional|should\s+i\s+not\s+send|what\s+(should|must)\s+i\s+not|sensitive\s+credential|walk\s+me\s+through|ask\s+me\s+(questions?|one)|don'?t\s+give\s+me\s+all)\b/i;

export type VysorScopeVerdict = 'allowed' | 'blocked';

export interface VysorScopeOptions {
  attachmentText?: string;
  sessionMessages?: Array<{ role?: string; content?: string | null }>;
  /** Known chip / option ids from VYSOR — always in scope. */
  optionId?: string | null;
  /** App names from the user's catalog — allow bare name picks mid-flow. */
  catalogNames?: string[];
  /** Mid Setup/SSO/IGA — never show welcome canned reply. */
  midActiveFlow?: boolean;
}

const MID_FLOW_FOLLOWUP_RE =
  /\b(yes|yeah|yep|sure|please|fill|details?|review|submit|confirm|continue|show\s+me|before\s+submit|let\s+me\s+see|looks?\s+good|go\s+ahead|do\s+it|proceed)\b/i;

const KNOWN_OPTION_ID_RE =
  /^(pending_|pick_inst_|pick_app_|pick_other|proto_|start_sso_|start_setup_app|start_configure_sso|start_iga_entitlements|open_sso_form|setup_new_app|browse_hint|continue_chat|continue_sso|submit_sso|sso_attrs_|sso_review_|wz_|idp:|idp_use_|sso_saml|sso_oidc|app:|inst:|opt_|env_hint_)/i;

function isKnownOptionId(optionId?: string | null): boolean {
  return !!optionId && KNOWN_OPTION_ID_RE.test(optionId.trim());
}

function matchesCatalogName(text: string, catalogNames?: string[]): boolean {
  if (!catalogNames?.length) return false;
  const q = text.trim().toLowerCase();
  if (q.length < 2 || q.length > 80) return false;
  return catalogNames.some(name => {
    const n = name.trim().toLowerCase();
    return n === q || n.startsWith(q) || q.startsWith(n) || n.includes(q) || q.includes(n);
  });
}

function isMidFlowCatalogReply(
  text: string,
  sessionMessages?: Array<{ role?: string; content?: string | null }>,
): boolean {
  if (!hasRecentIamContext(sessionMessages)) return false;
  const t = text.trim();
  if (t.length > 200) return false;
  if (/^\d{1,3}([.)].*)?$/.test(t)) return true;
  if (/^show (next|previous)/i.test(t)) return true;
  if (/yes,?\s*start sso/i.test(t)) return true;
  if (MID_FLOW_FOLLOWUP_RE.test(t)) return true;
  return !isObviousOffTopic(t) && t.length <= 120;
}

function containsIamSignal(text: string): boolean {
  const lower = text.toLowerCase();
  return IAM_KEYWORDS.some(kw => lower.includes(kw));
}

function isObviousOffTopic(text: string): boolean {
  if (containsIamSignal(text)) return false;
  return OFF_TOPIC_RE.some(re => re.test(text));
}

function isShortIamFollowUp(text: string): boolean {
  const t = text.trim();
  if (t.length > 220) return false;
  if (/\?\s*$/.test(t)) return true;
  if (
    /^(which one|what about|and then|what next|can you|could you|please|yes|no|next|full|saml|oidc|yeah|yep|sure)\b/i.test(
      t,
    )
  ) {
    return true;
  }
  return MID_FLOW_FOLLOWUP_RE.test(t);
}

function hasRecentIamContext(
  sessionMessages?: Array<{ role?: string; content?: string | null }>,
): boolean {
  if (!sessionMessages?.length) return false;
  return sessionMessages.slice(-8).some(m => {
    const text = (typeof m.content === 'string' ? m.content : '').trim();
    return (
      text.length > 0 &&
      (containsIamSignal(text) || WIZARD_HEADER.test(text) || VYSOR_CONTEXT_HEADER.test(text))
    );
  });
}

export function classifyVysorMessageScope(
  message: string,
  options: VysorScopeOptions = {},
): VysorScopeVerdict {
  const trimmed = message.trim();
  if (!trimmed && !options.attachmentText?.trim()) return 'blocked';
  if (options.attachmentText?.trim()) return 'allowed';
  if (isKnownOptionId(options.optionId)) return 'allowed';
  if (WIZARD_HEADER.test(trimmed) || VYSOR_CONTEXT_HEADER.test(trimmed)) return 'allowed';
  if (options.midActiveFlow && !isObviousOffTopic(trimmed)) return 'allowed';
  if (GREETING_RE.test(trimmed) || VYSOR_HELP_RE.test(trimmed) || SOCIAL_ACK_RE.test(trimmed)) {
    return 'allowed';
  }
  if (
    CONFUSION_RE.test(trimmed) ||
    UNCERTAIN_RE.test(trimmed) ||
    /\bidk\b/i.test(trimmed) ||
    /\b(no idea|have no idea|not a clue)\b/i.test(trimmed) ||
    /\bwhere\s+(can|do|to|would|i\s+can)\s+.{0,40}\b(get|find|see|look)\b/i.test(trimmed) ||
    /\bhow\s+(do|can)\s+i\s+(get|find|choose|pick|know)\b/i.test(trimmed) ||
    /^(huh|what\?+)\s*$/i.test(trimmed) ||
    /^(what do you mean|explain( that| again)?)\b/i.test(trimmed)
  ) {
    return 'allowed';
  }
  if (CONVERSATIONAL_GUIDE_RE.test(trimmed) && !isObviousOffTopic(trimmed)) {
    return 'allowed';
  }
  if (
    /\b(walk me through|one (question|step) at a time|step[\s-]by[\s-]step|i'?m not technical|non-?technical)\b/i.test(
      trimmed,
    ) &&
    !isObviousOffTopic(trimmed)
  ) {
    return 'allowed';
  }
  if (ONBOARDING_HELP_RE.test(trimmed) && !isObviousOffTopic(trimmed)) {
    return 'allowed';
  }
  if (/\?\s*$/.test(trimmed) && trimmed.length >= 12 && !isObviousOffTopic(trimmed)) {
    return 'allowed';
  }
  if (VYSOR_COMMAND_RE.test(trimmed) || UUID_RE.test(trimmed)) return 'allowed';
  if (containsIamSignal(trimmed)) return 'allowed';
  if (matchesCatalogName(trimmed, options.catalogNames)) return 'allowed';
  if (hasRecentIamContext(options.sessionMessages) && isShortIamFollowUp(trimmed)) {
    return 'allowed';
  }
  if (isMidFlowCatalogReply(trimmed, options.sessionMessages)) return 'allowed';
  if (hasRecentIamContext(options.sessionMessages) && !isObviousOffTopic(trimmed)) {
    return 'allowed';
  }
  if (isObviousOffTopic(trimmed)) return 'blocked';
  if (trimmed.length >= 10) return 'blocked';
  return 'allowed';
}

export function getVysorOutOfScopeReply(): string {
  return VYSOR_OUT_OF_SCOPE_REPLY;
}
